import apiClient from './client';
import type { AuditEvent, Job } from '../types';

export interface CreateJobRequest {
  chapter_id: string;
  telegram_id?: number;
}

export const jobsApi = {
  getAll: async (params?: { series_id?: string; chapter_id?: string; limit?: number }): Promise<Job[]> => {
    const queryParams = new URLSearchParams();
    if (params?.series_id) queryParams.append('series_id', params.series_id);
    if (params?.chapter_id) queryParams.append('chapter_id', params.chapter_id);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    
    const query = queryParams.toString();
    const url = query ? `/jobs?${query}` : '/jobs';
    const response = await apiClient.get<Job[]>(url);
    return response.data;
  },

  getById: async (id: string): Promise<Job> => {
    const response = await apiClient.get<Job>(`/jobs/${id}`);
    return response.data;
  },

  getAudit: async (id: string, params?: { limit?: number }): Promise<AuditEvent[]> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', String(params.limit));
    const query = queryParams.toString();
    const url = query ? `/jobs/${id}/audit?${query}` : `/jobs/${id}/audit`;
    const response = await apiClient.get<AuditEvent[]>(url);
    return response.data;
  },

  create: async (data: CreateJobRequest): Promise<{ id: string }> => {
    const response = await apiClient.post<{ id: string }>('/jobs', data);
    return response.data;
  },

  retry: async (id: string): Promise<{ id: string; status: string; message: string }> => {
    const response = await apiClient.post<{ id: string; status: string; message: string }>(`/jobs/${id}/retry`);
    return response.data;
  },

  downloadResult: async (id: string, format: 'txt' | 'json' | 'docx'): Promise<{ blob: Blob; filename: string }> => {
    const response = await apiClient.get(`/jobs/${id}/result?format=${format}`, {
      responseType: 'blob',
    });
    
    // Check if blob is empty
    if (!response.data || (response.data instanceof Blob && response.data.size === 0)) {
      throw new Error(`Файл пуст или не найден. Проверьте, что задача завершена и файл создан.`);
    }
    
    const cd = (response.headers?.['content-disposition'] as string | undefined) || '';
    // Try to extract filename from Content-Disposition header.
    // IMPORTANT: Prefer RFC 5987 `filename*` (UTF-8) over ASCII `filename`.
    const mStar = cd.match(/filename\*=UTF-8''([^;]+)/i);
    const mQuoted = cd.match(/filename="([^"]+)"/i);
    const filename =
      (mStar ? decodeURIComponent(mStar[1]) : null) ||
      mQuoted?.[1] ||
      `job_${id}.${format}`;
    return { blob: response.data as Blob, filename };
  },

  pause: async (id: string): Promise<{ id: string; status: string; message: string }> => {
    const response = await apiClient.post<{ id: string; status: string; message: string }>(`/jobs/${id}/pause`);
    return response.data;
  },

  resume: async (id: string): Promise<{ id: string; status: string; message: string }> => {
    const response = await apiClient.post<{ id: string; status: string; message: string }>(`/jobs/${id}/resume`);
    return response.data;
  },

  remove: async (id: string): Promise<{ id: string; status: string; message: string }> => {
    const response = await apiClient.post<{ id: string; status: string; message: string }>(`/jobs/${id}/remove`);
    return response.data;
  },

  batchAction: async (jobIds: string[], action: 'retry' | 'remove' | 'pause' | 'resume'): Promise<{ results: Array<{ id: string; ok: boolean; error?: string }> }> => {
    const response = await apiClient.post<{ results: Array<{ id: string; ok: boolean; error?: string }> }>('/jobs/batch-action', { job_ids: jobIds, action });
    return response.data;
  },
};
